import { PageShell } from '../components';
import { PageBody, PageHero, Prose } from '../components/page';

const sections = [
  {
    title: 'Service Description',
    paragraphs: [
      'Statio is a managed platform for securing AI agent API access. It provides encrypted credential storage, access controls, audit trails, and a multi-tenant gateway for MCP (Model Context Protocol) servers.',
      'Statio is provided by SID Technologies ("we", "us", "our"). By using Statio, you agree to these terms.',
    ],
  },
  {
    title: 'Account Responsibilities',
    paragraphs: [
      'You are responsible for maintaining the security of your account credentials, including your Statio API tokens and any passwords associated with your account.',
      'You must provide accurate and complete information when creating your account. You agree to update your information promptly if it changes.',
      'You are responsible for all activity that occurs under your account, whether or not you authorized it. Notify us immediately at dans@romans.dev if you suspect unauthorized access.',
    ],
  },
  {
    title: 'Acceptable Use',
    paragraphs: [
      'You may use Statio to store credentials, route API calls for your AI agents, enforce access policies, and generate audit trails in accordance with your plan.',
      'You may not use Statio to proxy or facilitate illegal activity, distribute malware, launch denial-of-service attacks, or circumvent the security measures of third-party APIs.',
      'You may not attempt to reverse-engineer, decompile, or otherwise extract the source code of Statio’s proprietary systems.',
      'You may not resell or sublicense access to Statio without our written consent.',
    ],
  },
  {
    title: 'Credential Storage',
    paragraphs: [
      'Statio encrypts and stores the API credentials you provide. You are solely responsible for the credentials you store with Statio, including ensuring you have authorization to use them and that they comply with the terms of the respective API providers.',
      'We do not verify the validity or authorization of credentials you store. We act as a secure proxy and are not a party to your agreements with third-party API providers.',
      'You should rotate credentials regularly and revoke access for any compromised credentials immediately.',
    ],
  },
  {
    title: 'Rate Limiting and Fair Use',
    paragraphs: [
      'Each plan has defined limits for MCP servers and API calls per month. Calls beyond your included allowance are refused until the next billing period, unless you enable overage billing, in which case they are billed at the published rate up to the spend cap you set.',
      'We reserve the right to throttle or suspend accounts that engage in abusive usage patterns that degrade service quality for other users, even if within stated plan limits.',
    ],
  },
  {
    title: 'Payment Terms',
    paragraphs: [
      'Paid plans are billed monthly or annually in advance. If you enable overage billing, usage beyond your included allowance is billed in arrears at the end of each billing period, at the published rate and never above the spend cap you set. All fees are non-refundable except as required by law.',
      'We may change pricing with 30 days’ notice. Price changes do not affect the current billing period.',
      'If a payment fails, we will retry the charge. If it remains unpaid, we may suspend paid features or your account until the balance is settled. Suspension does not delete your data; deletion follows the termination terms below.',
    ],
  },
  {
    title: 'Limitation of Liability',
    paragraphs: [
      'Statio is provided "as is" without warranty of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.',
      'To the maximum extent permitted by law, SID Technologies shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities.',
      'Our total liability for any claim arising from or related to your use of Statio shall not exceed the amount you paid us in the 12 months preceding the claim.',
    ],
  },
  {
    title: 'Termination',
    paragraphs: [
      'You may cancel your account at any time. Upon cancellation, your access continues until the end of the current billing period.',
      'We may terminate or suspend your account immediately if you violate these terms. We will make reasonable efforts to notify you before or at the time of termination.',
      'Upon termination, all stored credentials are permanently deleted. Audit logs are retained for the period specified by your plan, then deleted.',
    ],
  },
  {
    title: 'Changes to These Terms',
    paragraphs: [
      'We may update these terms from time to time. We will notify you of material changes via email or through the Statio dashboard at least 14 days before they take effect.',
      'Continued use of Statio after changes take effect constitutes acceptance of the updated terms.',
    ],
  },
];

export default function TermsPage() {
  return (
    <PageShell>
      <PageBody narrow>
        <PageHero title="Terms of Service" lede="Short, readable terms for using Statio, without legalese walls.">
          <p className="pg-row-meta">Effective: March 2026</p>
        </PageHero>
        <Prose>
          {sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </section>
          ))}
          <section>
            <h2>Contact</h2>
            <p>
              Questions about these terms? Contact us at <a href="mailto:dans@romans.dev">dans@romans.dev</a>.
            </p>
          </section>
        </Prose>
      </PageBody>
    </PageShell>
  );
}
